import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { subscribeSbpAnchor } from './anchorBus'

/**
 * Searchbar Pro desktop popover anchoring (#870 follow-up, Eli QA item 1).
 *
 * The toolkit reference renders When/Who/Filters as popovers anchored under
 * the bar with NO scrim (searchbar-pro.css:887-903 guests, 1054-1071 what,
 * 1780-1796 datepicker); the port shipped them as viewport-centred modals
 * over a rgba(0,0,0,0.6) overlay, so only Where read as a dropdown. The
 * reference gets anchoring for free from `position: absolute` inside the
 * bar; our dialogs are PORTALED (elevated shadow host, #691), so the same
 * shape needs the bar's viewport rect measured on open and written onto the
 * dialog wrapper as fixed coordinates — plus re-measures while open, because
 * a fixed-position panel will not follow the page.
 *
 * Gated on the SBP bar by construction: only ProFilterBar's DESKTOP field
 * instances pass `enabled`, and `measure` additionally requires the trigger
 * to sit inside a laid-out `.hfse-sbp-bar`. The legacy explorer, the mobile
 * sheet's staged fields (their triggers live in `.hfse-sbp-mobile-stage`),
 * and a bar hidden below the 933px breakpoint all fall back to the centred
 * modal untouched.
 */

/** Vertical gap between bar and popover — toolkit `top: calc(100% + 8px)`
 *  (searchbar-pro.css:892, 1060, 1786). */
export const SBP_POPOVER_GAP_PX = 8
/** Right-anchored popovers (Who / Filters) — toolkit `width: 50%;
 *  min-width: 320px` of the bar (searchbar-pro.css:894-895, 1062-1063). */
export const SBP_POPOVER_SIDE_FRACTION = 0.5
export const SBP_POPOVER_SIDE_MIN_PX = 320
/** Breathing room kept between the popover and the viewport bottom. The
 *  reference has no equivalent (its absolute panels scroll with the page);
 *  a fixed panel that overran the viewport would just clip. */
export const SBP_POPOVER_VIEWPORT_PAD_PX = 16
/** Below this much usable height, an anchored panel is not worth anchoring:
 *  the guest picker's own padding is ~48px of it and the date picker needs a
 *  calendar grid, so a shorter box is a sliver clipped to the viewport edge
 *  with its rows spilling out of reach — Radix locks body scroll, so the
 *  visitor cannot scroll it into view either. Falling back to the centred
 *  modal (the same null path the <933px breakpoint already takes) always
 *  fits. Reachable on any short desktop window: a 1440x900 page scrolled so
 *  the bar sits at top 780 leaves 36px. */
export const SBP_POPOVER_MIN_H_PX = 240
/** Content floor for the 'bar' anchor. DELIBERATE DIVERGENCE from the toolkit's
 *  full-bar-width datepicker (searchbar-pro.css:1780-1796): the reference panel
 *  is `left: 0; right: 0` of the bar and nothing else, but the bar is
 *  `min(850px, container)` and the picker's contents are not compressible below
 *  a fixed sum — two month panes of seven 44px cells (2 × 308) + the 24px
 *  inter-month gutter + the card's 24px padding pair + its 1px border pair =
 *  690. Under that the panes squeezed and the week rows OVERLAPPED (#1360: 29px
 *  at a 1280 viewport, where the bar measures 584). The divergence is
 *  one-directional — this floor only ever GROWS the panel, never shrinks a wide
 *  bar — and the panel stays centred on the bar, which is itself `margin: 0
 *  auto`. `searchbar-datepicker.test.tsx` recomputes this sum from the two
 *  stylesheets so it cannot silently rot when a cell or a padding moves. */
export const SBP_BAR_POPOVER_MIN_W_PX = 690

/** 'bar' spans the full bar width (When — toolkit datepicker `left:0;
 *  right:0`), or SBP_BAR_POPOVER_MIN_W_PX centred on it where the bar is too
 *  narrow to hold two months; 'right' hugs the bar's right edge at half its
 *  width (Who / Filters — toolkit guests/what popovers). */
export type SbpPopoverAnchor = 'bar' | 'right'

export interface SbpPopoverRect {
  left: number
  top: number
  width: number
  maxHeight: number
}

/**
 * The popover box for a trigger, in viewport (position:fixed) coordinates,
 * or null when the trigger is not inside a laid-out SBP bar — which is the
 * whole desktop/mobile/legacy gate: below the breakpoint the bar is
 * display:none and measures 0 wide, and the mobile sheet's staged triggers
 * have no `.hfse-sbp-bar` ancestor at all.
 */
export function measureSbpPopover(
  trigger: HTMLElement | null,
  anchor: SbpPopoverAnchor,
): SbpPopoverRect | null {
  const bar = trigger?.closest?.('.hfse-sbp-bar') as HTMLElement | null
  if (!bar) return null
  const rect = bar.getBoundingClientRect()
  // Negated `>` rather than `=== 0`: it also rejects a NaN rect, which would
  // otherwise propagate into left/top as NaN pixel values.
  if (!(rect.width > 0) || !(rect.height > 0)) return null
  const top = rect.bottom + SBP_POPOVER_GAP_PX
  const maxHeight = window.innerHeight - top - SBP_POPOVER_VIEWPORT_PAD_PX
  if (maxHeight < SBP_POPOVER_MIN_H_PX) return null
  if (anchor === 'bar') {
    // Widen a bar too narrow to hold the calendar, but never past the viewport:
    // innerWidth (not documentElement.clientWidth, which happy-dom reports as 0)
    // counts the classic scrollbar, and the 16px pad absorbs it.
    const width = Math.min(
      Math.max(rect.width, SBP_BAR_POPOVER_MIN_W_PX),
      window.innerWidth - 2 * SBP_POPOVER_VIEWPORT_PAD_PX,
    )
    // The extra width is split either side of the bar rather than hung off its
    // right edge: the bar is page-centred, so a bar-centred panel still reads as
    // deliberate at the widths where the two cannot share an edge.
    const centred = rect.left - (width - rect.width) / 2
    const left = Math.max(
      SBP_POPOVER_VIEWPORT_PAD_PX,
      Math.min(centred, window.innerWidth - SBP_POPOVER_VIEWPORT_PAD_PX - width),
    )
    return { left, top, width, maxHeight }
  }
  // min-width can exceed 50% of a narrow bar, but never the bar itself.
  const width = Math.min(
    Math.max(rect.width * SBP_POPOVER_SIDE_FRACTION, SBP_POPOVER_SIDE_MIN_PX),
    rect.width,
  )
  return { left: rect.right - width, top, width, maxHeight }
}

export interface SbpPopoverBinding {
  /** Goes on the field's trigger button (Radix composes it with its own). */
  triggerRef: (node: HTMLElement | null) => void
  /** Call at the OPEN flip, inside onOpenChange, so the measurement state
   *  batches with the caller's setOpen and the portal's very first paint is
   *  already anchored — an effect would flash one centred frame. */
  measureNow: () => void
  /** True when a live measurement is anchoring the open dialog. */
  anchored: boolean
  /** Merge target for DialogContent's wrapperStyle. Undefined when not
   *  anchored, so the centred-modal base style stays in charge. */
  wrapperStyle: CSSProperties | undefined
}

export function useSbpPopover(
  enabled: boolean,
  anchor: SbpPopoverAnchor,
  open: boolean,
): SbpPopoverBinding {
  const triggerEl = useRef<HTMLElement | null>(null)
  const triggerRef = useCallback((node: HTMLElement | null) => {
    triggerEl.current = node
  }, [])
  const [rect, setRect] = useState<SbpPopoverRect | null>(null)

  const measureNow = useCallback(() => {
    if (!enabled) return
    setRect(measureSbpPopover(triggerEl.current, anchor))
  }, [enabled, anchor])

  // Re-measure while open: scroll (capture, for scrolling containers — Radix
  // locks body scroll but not every host page honors it), window resize, and
  // bar reflow (the CTA label re-measuring on a totals update can re-flex the
  // fields without any viewport change). A resize across the 933px breakpoint
  // hides the bar, measure() returns null, and the dialog degrades to the
  // centred modal + scrim rather than floating at stale coordinates.
  useEffect(() => {
    if (!enabled || !open) return
    // Bail out of the state update when nothing moved: observing an ancestor
    // (below) is a noisier signal than observing the bar alone, and without
    // this guard every unrelated ancestor resize would re-render the open
    // dialog.
    const sync = () => setRect((prev) => {
      const next = measureSbpPopover(triggerEl.current, anchor)
      if (prev && next
        && prev.left === next.left && prev.top === next.top
        && prev.width === next.width && prev.maxHeight === next.maxHeight) return prev
      return next
    })
    window.addEventListener('resize', sync)
    window.addEventListener('scroll', sync, true)
    // A sticky pin/unpin or morph moves the bar after this scroll event's
    // sync already ran (#1220 item 1) — ProFilterBar publishes once the
    // sticky DOM has committed.
    const unsubscribe = subscribeSbpAnchor(sync)
    const bar = triggerEl.current?.closest?.('.hfse-sbp-bar') as HTMLElement | null
    const resize = typeof ResizeObserver !== 'undefined' && bar ? new ResizeObserver(sync) : null
    if (resize && bar) {
      resize.observe(bar)
      // A ResizeObserver on the bar sees the bar's OWN size change, not a
      // position-only shift. Content appearing or vanishing ABOVE the bar
      // moves it without resizing it — e.g. clearing guests from inside an
      // open Who popover refetches, the result count crosses zero, and the
      // section heading mounts or unmounts above the widget. That reflows the
      // container, so observing it catches what observing the bar cannot.
      const container = bar.closest('.hfse') as HTMLElement | null
      if (container && container !== bar) resize.observe(container)
    }
    return () => {
      window.removeEventListener('resize', sync)
      window.removeEventListener('scroll', sync, true)
      unsubscribe()
      resize?.disconnect()
    }
  }, [enabled, open, anchor])

  const anchored = enabled && rect !== null
  // Decision-22 note: the ready design assigned per-field z-orders
  // (9999/9998/9997) for the overlap-during-swap frame, but Radix unmounts a
  // closing dialog's wrapper instantly (see the Presence note in
  // explorer.css), so two popovers can never coexist — the wrapper keeps its
  // base z-index. `--hfse-sbp-popover-max-h` inherits into the content card,
  // where the `.hfse-sbp-popover-anchored` rule consumes it: a percentage
  // max-height on the child would resolve against this auto-height wrapper
  // and silently become none.
  const wrapperStyle: CSSProperties | undefined = anchored
    ? ({
        inset: 'auto',
        left: rect!.left,
        top: rect!.top,
        width: rect!.width,
        padding: 0,
        '--hfse-sbp-popover-max-h': `${rect!.maxHeight}px`,
      } as CSSProperties)
    : undefined

  return { triggerRef, measureNow, anchored, wrapperStyle }
}
